import React, { useState } from "react";
import {
  Box,
  Grid,
  Button,
  MenuItem,
  TextField,
  Typography,
  FormControlLabel,
  Checkbox,
} from "@mui/material";

import dayjs from "dayjs";

import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";

const weekDays = [
  { label: "Sunday", value: 0 },
  { label: "Monday", value: 1 },
  { label: "Tuesday", value: 2 },
  { label: "Wednesday", value: 3 },
  { label: "Thursday", value: 4 },
  { label: "Friday", value: 5 },
  { label: "Saturday", value: 6 },
];

export default function EditScheduleReport({onReceiveProps}) {
  const [formData, setFormData] = useState({
    reportName: "Sample Data",
    reportType: "Candidate Reports",
    frequency: "daily",
    scheduleTime: dayjs().hour(9).minute(0),
    startDate: dayjs("2025-05-08"),
    endDate: dayjs("2025-05-18"),
    weeklyDays: [0, 1],
  });

  const [errors, setErrors] = useState({});

  const validateForm = () => {
    let tempErrors = {};
    let isValid = true;

    if (!formData.frequency) {
      tempErrors.frequency = "Frequency is required";
      isValid = false;
    }

    if (
      formData.frequency === "weekly" &&
      formData.weeklyDays.length === 0
    ) {
      tempErrors.weeklyDays = "Please select at least one day";
      isValid = false;
    }

    if (!formData.scheduleTime) {
      tempErrors.scheduleTime = "Schedule time is required";
      isValid = false;
    }

    if (!formData.startDate) {
      tempErrors.startDate = "Start date is required";
      isValid = false;
    }

    if (
      formData.endDate &&
      dayjs(formData.endDate).isBefore(formData.startDate, "day")
    ) {
      tempErrors.endDate = "End date must be after start date";
      isValid = false;
    }

    setErrors(tempErrors);
    return isValid;
  };

  const handleSave = () => {
    if (!validateForm()) return;

    const payload = {
      ...formData,
      scheduleTime: dayjs(formData.scheduleTime).format("HH:mm:ss"),
      startDate: dayjs(formData.startDate).format("YYYY-MM-DD"),
      endDate: formData.endDate
        ? dayjs(formData.endDate).format("YYYY-MM-DD")
        : "",
    };

    console.log("Updated Payload", payload);
  };

  const handleWeeklyDay = (value) => {
    let updatedDays = [];

    if (formData.weeklyDays.includes(value)) {
      updatedDays = formData.weeklyDays.filter((d) => d !== value);
    } else {
      updatedDays = [...formData.weeklyDays, value];
    }

    setFormData({
      ...formData,
      weeklyDays: updatedDays,
    });

    if (updatedDays.length > 0) {
      setErrors((prev) => ({
        ...prev,
        weeklyDays: null,
      }));
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        sx={{
          p: 3,
          background: "#fff",
          borderRadius: "12px",
        }}
      >
        <Grid container spacing={3}>
          {/* Report Name */}
          <Grid item xs={12} md={6}>
            <TextField
              label="Report Name"
              fullWidth
              disabled
              value={formData.reportName}
            />
          </Grid>

          {/* Report Type */}
          <Grid item xs={12} md={6}>
            <TextField
              label="Report Type"
              fullWidth
              disabled
              value={formData.reportType}
            />
          </Grid>

          {/* Frequency */}
          <Grid item xs={12} md={6}>
            <TextField
              label="Frequency"
              select
              fullWidth
              value={formData.frequency}
              error={!!errors.frequency}
              helperText={errors.frequency}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  frequency: e.target.value,
                })
              }
            >
              <MenuItem value="daily">Daily</MenuItem>
              <MenuItem value="weekly">Weekly</MenuItem>
              <MenuItem value="monthly">Monthly</MenuItem>
            </TextField>
          </Grid>

          {/* Schedule Time */}
          <Grid item xs={12} md={6}>
            <TimePicker
              label="Schedule"
              value={formData.scheduleTime}
              ampm
              closeOnSelect={false}
              desktopModeMediaQuery="@media (pointer: fine)"
              onChange={(newValue) => {
                setFormData({
                  ...formData,
                  scheduleTime: newValue,
                });

                if (newValue) {
                  setErrors((prev) => ({
                    ...prev,
                    scheduleTime: null,
                  }));
                }
              }}
              slotProps={{
                actionBar: {
                  actions: ["cancel", "accept"],
                },
                textField: {
                  fullWidth: true,
                  error: !!errors.scheduleTime,
                  helperText: errors.scheduleTime,
                },
              }}
            />
          </Grid>

          {/* Weekly Days */}
          {formData.frequency === "weekly" && (
            <Grid item xs={12}>
              <Typography
                sx={{
                  fontWeight: 600,
                  mb: 1,
                  textAlign: "left",
                }}
              >
                Select Days
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  gap: "0 2",
                  flexWrap: "wrap",
                }}
              >
                {weekDays.map((day) => (
                  <FormControlLabel
                    key={day.value}
                    control={
                      <Checkbox
                        checked={formData.weeklyDays.includes(
                          day.value
                        )}
                        onChange={() =>
                          handleWeeklyDay(day.value)
                        }
                      />
                    }
                    label={day.label}
                  />
                ))}
              </Box>

              {errors.weeklyDays && (
                <Typography
                  color="error"
                  variant="caption"
                >
                  {errors.weeklyDays}
                </Typography>
              )}
            </Grid>
          )}

          {/* Start Date */}
          <Grid item xs={12} md={6}>
            <DatePicker
              label="Start Date"
              value={formData.startDate}
              onChange={(newValue) => {
                setFormData({
                  ...formData,
                  startDate: newValue,
                });

                if (newValue) {
                  setErrors((prev) => ({
                    ...prev,
                    startDate: null,
                  }));
                }
              }}
              slotProps={{
                textField: {
                  fullWidth: true,
                  error: !!errors.startDate,
                  helperText: errors.startDate,
                },
              }}
            />
          </Grid>

          {/* End Date */}
          <Grid item xs={12} md={6}>
            <DatePicker
              label="End Date"
              value={formData.endDate}
              minDate={formData.startDate}
              onChange={(newValue) => {
                setFormData({
                  ...formData,
                  endDate: newValue,
                });

                if (newValue) {
                  setErrors((prev) => ({
                    ...prev,
                    endDate: null,
                  }));
                }
              }}
              slotProps={{
                textField: {
                  fullWidth: true,
                  error: !!errors.endDate,
                  helperText: errors.endDate,
                },
              }}
            />
          </Grid>
        </Grid>

        {/* Footer Buttons */}
        <Box
            id="clientsbuttons"
            sx={{
              mt: 4,
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              backgroundColor: "var(--secondary-bg-color)",
              padding: "15px 20px",
              margin: "40px -16px -16px -16px",
              borderBottomLeftRadius: "8px",
              borderBottomRightRadius: "8px",
            }}
          >
            <Button
              variant="text"
              className="clientcancelbtn"
              onClick={() => onReceiveProps()}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              className="clientssubmitbtn"
              type="submit"
              size="small"
              onClick={handleSave}
            >
             Update
            </Button>
          </Box>
      </Box>
    </LocalizationProvider>
  );
}